import { DEFAULT_USER_ROLE } from '@common/constants/request.constants'
import { LocalStorageKey } from '@common/enums/global.enums'
import { Role } from '@common/enums/request.enums'
import {
	Token,
	User
} from '@common/types/request.types'
import { LocalStorageUtils } from '@common/utils/localStorage.utils'
import {
	useMutation,
	useQuery
} from '@tanstack/react-query'
import {
	createContext,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react'
import { useAuthenticationApi } from '../api/auth.api'

type AuthContextType = {
	isAdmin: boolean,
	isAuthenticating: boolean,
	isTrainer: boolean,
	storeToken: (token: Token) => void,
	token: Token | null,
	user: User
}

/**
 * Returns the time until token needs to be refreshed - 5 minutes
 * If no token, return 5 minutes
 * @param token
 */
const getTimeout = (token: Token | null) => {
	const DEFAULT_TIMEOUT = 5 * 60 * 1000 //5 minutes
	return token ? token.exp - DEFAULT_TIMEOUT : DEFAULT_TIMEOUT
}

const AuthContext = createContext<AuthContextType>({
	isAdmin: false,
	isAuthenticating: true,
	isTrainer: false,
	storeToken: () => undefined,
	token: LocalStorageUtils.getToken(),
	user: LocalStorageUtils.getUser()
})

export const AuthProvider = ({ children }: PropsWithChildren) => {
	const { fetchRefresh, fetchToken } = useAuthenticationApi()
	const [token, setToken] = useState<Token | null>(LocalStorageUtils.getToken())
	const [user, setUser] = useState<User>(LocalStorageUtils.getUser())

	const onSuccess = (data: User & Token) => {
		const token: Token = { token: data.token, exp: data.exp }
		const user: User = {
			id: data.id,
			name: data.name,
			email: data.email,
			role: data.role,
			company: {
				id: data.company?.id,
				type: data.company?.type
			}
		}

		LocalStorageUtils.updateToken(token)
		setToken(token)

		localStorage.setItem(LocalStorageKey.ROLE, user.role || DEFAULT_USER_ROLE)
		setUser(user)
	}

	const onError = (error: Error) => console.error('Invalid token, user is not logged in', error)

	/**
	 * Stores a token obtained from the authentication service
	 * Enables the token query, which requires the token to be available for the request header
	 * @param newToken
	 */
	const storeToken = useCallback((newToken: Token) => {
		LocalStorageUtils.updateToken(newToken)
		setToken(newToken)
	}, [])

	// Without a stored token the request can only fail : the user has to log in first
	const { data, error, isError, isSuccess } = useQuery({
		queryKey: ['fetchToken'],
		queryFn: fetchToken,
		enabled: !!token
	})

	const { mutate: fetchRefreshMutate } = useMutation({
		mutationKey: ['fetchRefresh', token],
		mutationFn: fetchRefresh,
		onSuccess,
		onError
	})

	const value = useMemo((): AuthContextType => {
		return ({
			isAdmin: user.role === Role.ADMIN,
			isAuthenticating: !isSuccess && !isError,
			isTrainer: user.role === Role.USER,
			storeToken,
			token,
			user
		})
	}, [
		isError,
		isSuccess,
		storeToken,
		token,
		user
	])

	useEffect(() => {
		if (isSuccess) {
			onSuccess(data)
		}
	}, [data, isSuccess])

	useEffect(() => {
		if (isError) {
			onError(error)
		}
	}, [error, isError])

	useEffect(() => {
		if (!token) {
			return
		}

		const timeout = setTimeout(() => {
			fetchRefreshMutate()
		}, getTimeout(token))
		return () => clearTimeout(timeout)
	}, [token])

	return (
		<AuthContext.Provider value={value}>
			{children}
		</AuthContext.Provider>
	)
}

/**
 * Authentication context
 */
export const useAuth = (): AuthContextType => {
	const context = useContext(AuthContext)
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider')
	}
	return context
}
